import React, { useEffect, useState, useCallback } from "react";
import { http, formatApiError } from "@/lib/api";
import { CheckCircle, Lightning, MapPin, Camera } from "@phosphor-icons/react";

function formatUSD(n) {
    const v = parseFloat(n || 0);
    return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function CountdownPill({ deadline }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30 * 1000);
        return () => clearInterval(t);
    }, []);
    if (!deadline) return null;
    const ms = new Date(deadline).getTime() - now;
    if (ms <= 0) {
        return (
            <span className="text-xs px-2 py-1 rounded-sm bg-red-500/10 border border-red-500/30 text-red-300">
                Window expired
            </span>
        );
    }
    const m = Math.floor(ms / 60000);
    const label = m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
    return (
        <span className="text-xs px-2 py-1 rounded-sm bg-amber-500/10 border border-amber-500/30 text-amber-300 inline-flex items-center gap-1">
            <Lightning size={12} weight="fill" /> Claim within {label}
        </span>
    );
}

function LeadCard({ lead, onClaim, claiming, kind, onScheduleChanged }) {
    const projectAmt = lead.final_amount || lead.butler_quote_amount || lead.ai_quote_amount;
    const isClaimed = kind === "claimed";
    const isExclusive = kind === "exclusive";

    return (
        <div
            data-testid={`lead-card-${lead.id}`}
            className={`rounded-sm border p-5 ${
                isExclusive
                    ? "border-gold/40 bg-gradient-to-br from-gold/10 to-transparent"
                    : isClaimed
                    ? "border-emerald-500/30 bg-emerald-500/5"
                    : "border-white/10 bg-slate-900/40"
            }`}
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-slate-800 text-slate-300">
                            {lead.category || "Task"}
                        </span>
                        {isExclusive && (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-gold/20 text-gold border border-gold/40">
                                Exclusive
                            </span>
                        )}
                        {isClaimed && (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                                Yours
                            </span>
                        )}
                        {isExclusive && lead.lead_deadline && <CountdownPill deadline={lead.lead_deadline} />}
                    </div>
                    <h3 className="font-serif text-xl text-white">
                        {isClaimed ? lead.title : (lead.title || `${lead.category} project`)}
                    </h3>
                    <div className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                        <MapPin size={12} weight="fill" /> {lead.city || "—"}
                        {lead.photos?.length ? (
                            <span className="ml-3 inline-flex items-center gap-1 text-slate-500">
                                <Camera size={12} weight="fill" /> {lead.photos.length} photo{lead.photos.length > 1 ? "s" : ""}
                            </span>
                        ) : null}
                    </div>
                    <p className="text-sm text-slate-300 mt-3 leading-relaxed">
                        {lead.ai_project_summary || lead.description_preview || "AI summary not available."}
                    </p>
                    {isClaimed && (
                        <div className="mt-4 p-3 rounded-sm bg-slate-950/60 border border-white/5 text-sm space-y-1">
                            <div><span className="text-slate-500 text-xs uppercase tracking-widest">Client</span> &nbsp;{lead.client_name}</div>
                            {lead.client_email && <div><span className="text-slate-500 text-xs uppercase tracking-widest">Email</span> &nbsp;{lead.client_email}</div>}
                            {lead.address && <div><span className="text-slate-500 text-xs uppercase tracking-widest">Address</span> &nbsp;{lead.address}</div>}
                        </div>
                    )}
                    {isClaimed && (
                        <ClaimedScheduleStrip lead={lead} onChanged={onScheduleChanged} />
                    )}
                </div>
                <div className="text-right min-w-[140px]">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Project</div>
                    <div className="font-serif text-2xl gold-text">{formatUSD(projectAmt)}</div>
                    {!isClaimed && (
                        <button
                            data-testid={`claim-lead-${lead.id}`}
                            onClick={() => onClaim(lead.id)}
                            disabled={claiming === lead.id}
                            className="btn-gold text-xs px-4 py-2 mt-3 disabled:opacity-50"
                        >
                            {claiming === lead.id ? "Claiming…" : "Claim job"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function slotLabelClient(w) {
    if (!w) return "";
    const [a, b] = w.split("-").map((x) => parseInt(x, 10));
    const fmt = (h) => {
        const ap = h < 12 ? "AM" : "PM";
        const d = h <= 12 ? (h === 0 ? 12 : h) : h - 12;
        return `${d}:00 ${ap}`;
    };
    return `${fmt(a)} – ${fmt(b)}`;
}

function ClaimedScheduleStrip({ lead, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const status = lead.schedule_status || "none";
    const proposed = lead.proposed_slot;
    const confirmed = lead.confirmed_slot;

    async function confirm() {
        setBusy(true); setErr("");
        try {
            await http.post(`/tasks/${lead.id}/schedule/confirm`);
            if (onChanged) await onChanged();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function reject() {
        if (!window.confirm("Decline this time?")) return;
        setBusy(true); setErr("");
        try {
            await http.post(`/tasks/${lead.id}/schedule/reject`);
            if (onChanged) await onChanged();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    if (status === "confirmed" && confirmed) {
        return (
            <div data-testid={`claimed-schedule-${lead.id}`} className="mt-3 rounded-sm border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm flex items-center justify-between gap-3 flex-wrap">
                <span className="text-emerald-300">✓ Confirmed for <strong className="text-white">{confirmed.date} · {confirmed.label || slotLabelClient(confirmed.window)}</strong></span>
                <button data-testid={`reschedule-${lead.id}`} onClick={reject} disabled={busy} className="text-xs underline text-slate-300 hover:text-white disabled:opacity-50">
                    Reschedule
                </button>
            </div>
        );
    }
    if (status === "proposed" && proposed) {
        const proposedByMe = proposed.proposed_by === "partner";
        return (
            <div data-testid={`claimed-schedule-${lead.id}`} className="mt-3 rounded-sm border border-gold/40 bg-gold/5 p-3 text-sm">
                <div className="text-slate-300">
                    {proposedByMe ? "You proposed:" : "Client proposed:"} <strong className="text-white">{proposed.date} · {proposed.label || slotLabelClient(proposed.window)}</strong>
                </div>
                {err && <div className="text-red-400 text-xs mt-1">{err}</div>}
                {!proposedByMe ? (
                    <div className="mt-2 flex gap-2 flex-wrap">
                        <button data-testid={`confirm-schedule-${lead.id}`} onClick={confirm} disabled={busy} className="btn-gold text-xs px-3 py-1.5 disabled:opacity-50">
                            Confirm
                        </button>
                        <button data-testid={`decline-schedule-${lead.id}`} onClick={reject} disabled={busy} className="text-xs px-3 py-1.5 rounded-sm border border-white/15 bg-white/5 text-slate-200 hover:bg-white/10 disabled:opacity-50">
                            Decline
                        </button>
                    </div>
                ) : (
                    <div className="mt-1 text-xs text-slate-400 inline-flex items-center gap-2">
                        Waiting on client.
                        <button onClick={reject} disabled={busy} className="underline hover:text-white">Cancel</button>
                    </div>
                )}
            </div>
        );
    }
    return (
        <div data-testid={`claimed-schedule-${lead.id}`} className="mt-3 text-xs text-slate-400">
            Homeowner can now pick a 2-hour appointment window. We&apos;ll email you to confirm.
        </div>
    );
}

export default function PartnerLeadsPanel({ onOpenChange }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [claiming, setClaiming] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await http.get("/partner/leads");
            setData(data);
            if (onOpenChange) onOpenChange(data.is_open_for_work);
        } catch (e) {
            setError(formatApiError(e));
        }
    }, [onOpenChange]);

    useEffect(() => { load(); }, [load]);

    async function toggleAvailability() {
        if (!data) return;
        setBusy(true); setError("");
        try {
            const next = !data.is_open_for_work;
            await http.post("/partner/availability", { is_open_for_work: next });
            await load();
        } catch (e) { setError(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function claimLead(taskId) {
        setClaiming(taskId); setError("");
        try {
            await http.post(`/partner/leads/${taskId}/claim`);
            await load();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setClaiming(null);
        }
    }

    if (!data) {
        return <div className="text-slate-500 text-sm py-6">Loading leads…</div>;
    }

    const isOpen = !!data.is_open_for_work;
    const counts = {
        exclusive: data.exclusive?.length || 0,
        open: data.open?.length || 0,
        claimed: data.claimed?.length || 0,
    };

    return (
        <div data-testid="partner-leads-panel" className="space-y-6">
            {/* Availability toggle */}
            <div
                data-testid="availability-row"
                className={`rounded-sm border p-5 flex flex-wrap items-center gap-4 ${
                    isOpen
                        ? "border-emerald-500/30 bg-emerald-500/5"
                        : "border-white/10 bg-slate-900/40"
                }`}
            >
                <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${isOpen ? "bg-emerald-400" : "bg-slate-500"}`} />
                        <div className="text-sm font-medium text-white">
                            {isOpen ? "Open for work" : "Not accepting new leads"}
                        </div>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                        {isOpen
                            ? "Butler Room will route exclusive leads in your city + categories to you first."
                            : "Turn this on to start receiving exclusive lead offers in your area."}
                    </div>
                </div>
                <button
                    data-testid="toggle-availability"
                    onClick={toggleAvailability}
                    disabled={busy}
                    className={`text-xs px-4 py-2 rounded-sm border disabled:opacity-50 ${
                        isOpen
                            ? "border-white/15 bg-white/5 text-slate-200 hover:bg-white/10"
                            : "border-gold/40 bg-gold/15 text-gold hover:bg-gold/25"
                    }`}
                >
                    {busy ? "Saving…" : isOpen ? "Pause leads" : "Open for work"}
                </button>
            </div>

            {error && <div className="text-sm text-red-400">{error}</div>}

            {/* Exclusive (assigned to me) */}
            <section data-testid="exclusive-leads">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-serif text-xl flex items-center gap-2">
                        Exclusive offers
                        <span className="text-xs text-slate-500">({counts.exclusive})</span>
                    </h3>
                    {counts.exclusive > 0 && (
                        <span className="text-xs text-amber-300 inline-flex items-center gap-1">
                            <Lightning size={12} weight="fill" /> Act fast — 1-hour window
                        </span>
                    )}
                </div>
                {counts.exclusive === 0 ? (
                    <div className="text-sm text-slate-500 italic py-6">
                        No exclusive offers right now. We&apos;ll email you the moment one lands.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {data.exclusive.map((l) => (
                            <LeadCard key={l.id} lead={l} onClaim={claimLead} claiming={claiming} kind="exclusive" />
                        ))}
                    </div>
                )}
            </section>

            {/* Open pool */}
            <section data-testid="open-leads">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-serif text-xl flex items-center gap-2">
                        Open pool
                        <span className="text-xs text-slate-500">({counts.open})</span>
                    </h3>
                    <span className="text-xs text-slate-500">First to claim wins</span>
                </div>
                {counts.open === 0 ? (
                    <div className="text-sm text-slate-500 italic py-6">No open leads right now.</div>
                ) : (
                    <div className="space-y-3">
                        {data.open.map((l) => (
                            <LeadCard key={l.id} lead={l} onClaim={claimLead} claiming={claiming} kind="open" />
                        ))}
                    </div>
                )}
            </section>

            {/* Claimed */}
            {counts.claimed > 0 && (
                <section data-testid="claimed-leads">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-serif text-xl flex items-center gap-2">
                            Claimed jobs
                            <span className="text-xs text-slate-500">({counts.claimed})</span>
                            <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                        </h3>
                    </div>
                    <div className="space-y-3">
                        {data.claimed.map((l) => (
                            <LeadCard key={l.id} lead={l} onClaim={() => {}} claiming={null} kind="claimed" onScheduleChanged={load} />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
